// @ts-nocheck
/**
 * Template V2 — component node discriminated union and all concrete component types.
 *
 * Every component extends ComponentBase (id, type, layout, style, visibility, etc.).
 * The `type` field is the discriminant; concrete interfaces narrow it to a literal.
 * GroupComponent.group.children is a recursive reference to ComponentNode.
 */

import type { AssetRef } from './asset';
import type { DataBinding, ValueSource, ValueOrExpression } from './binding';
import type { PressHandlerInput } from './eventHandler';
import type { LocalizedText } from './metadata';
import type { TablePropertySchema } from './category';
import type { TemplateFontId } from './templateFonts';

/** All supported component type discriminant literals. */
export type ComponentType =
  | 'text'
  | 'number'
  | 'boolean'
  | 'date'
  | 'enum'
  | 'stringSelect'
  | 'image'
  | 'svg'
  | 'html'
  | 'qrCode'
  | 'rating'
  | 'color'
  | 'button'
  | 'icon'
  | 'badge'
  | 'progress'
  | 'timerDisplay'
  | 'slider'
  | 'group'
  | 'spacer'
  | 'dragHandle'
  | 'line'
  | 'customToken'
  | 'table'
  | 'chart'
  | 'listView';

/** Absolute-positioned layout box; all V2 component nodes use absolute layout within the item surface. Coordinate values are normalized — see field docs. */
export interface LayoutBox {
  /** Must be 'absolute' — the only supported layout mode in V2. */
  position: 'absolute';
  /** Horizontal offset as a fraction of the item surface width. Range [0..1]. */
  x: number;
  /** Vertical offset as a fraction of the item surface height. Range [0..1]. */
  y: number;
  /** Component width as a fraction of the item surface width. 1 = 100%. */
  width: number;
  /** Component height as a fraction of the item surface height. 1 = 100%. */
  height: number;
  /** Z-axis stacking order within the layout. */
  zIndex?: number;
  /** Anchor point used when interpreting x/y relative to the item surface. */
  anchor?:
    | 'topLeft'
    | 'topCenter'
    | 'topRight'
    | 'centerLeft'
    | 'center'
    | 'centerRight'
    | 'bottomLeft'
    | 'bottomCenter'
    | 'bottomRight';
  /** Rotation angle in degrees; static, binding, or expression-driven. */
  rotation?: ValueSource<number>;
}

/** Base properties shared by every component node variant. */
export interface ComponentBase {
  /** Unique identifier for this component within its parent layout. */
  id: string;
  /** Component type discriminant. */
  type: ComponentType;
  /** Layout position and size in normalized [0..1] coordinate space relative to the item surface. */
  layout: LayoutBox;
  /** Optional data binding for reading or writing a value. */
  binding?: DataBinding;
  /** Optional visual style overrides. */
  style?: StyleBlock;
  /** Optional declarative visibility rule. */
  visibility?: VisibilityRule;
  /** Optional declarative enabled/disabled rule. */
  enabled?: EnabledRule;
  /** Optional interaction bindings triggered by user gestures. */
  interactions?: ComponentInteractionBindings;
  /** Optional accessibility semantics for screen readers. */
  semantics?: ComponentSemantics;
}

/** Visual style overrides applicable to most component node variants. */
export interface StyleBlock {
  /** Foreground or text color; static, binding, or expression-driven. */
  color?: ValueSource<string>;
  /** Background fill color; static, binding, or expression-driven. */
  backgroundColor?: ValueSource<string>;
  /** Font size in logical pixels; static, binding, or expression-driven. */
  fontSize?: ValueSource<number>;
  /**
   * When true, scale resolved `fontSize` by min(itemW/orgW, itemH/orgH) so text
   * stays proportional to the item's creation baseline size.
   */
  scaleFontWithItemSize?: ValueSource<boolean>;
  /** Font weight preset. */
  fontWeight?: 'normal' | 'bold' | 'semibold' | 'light';
  /**
   * Logical typeface from the closed template font catalog (DEC-064).
   * Plain enum like `fontWeight` — not a ValueSource. Omitted → `nunitoSans` at resolve.
   * Never persist platform PostScript / CSS font-family strings here.
   */
  fontFamily?: TemplateFontId;
  /** Text alignment within the component bounds. Maps V1 textOptions.textAlign. */
  textAlign?: 'left' | 'center' | 'right' | 'justify';
  /** Border color; static, binding, or expression-driven. */
  borderColor?: ValueSource<string>;
  /** Border width in logical pixels; static, binding, or expression-driven. */
  borderWidth?: ValueSource<number>;
  /** Border corner radius; static, binding, or expression-driven. */
  borderRadius?: ValueSource<number>;
  /** Opacity in the range 0–1; static, binding, or expression-driven. */
  opacity?: ValueSource<number>;
  /** Padding — uniform number or per-side object. */
  padding?: number | { top?: number; bottom?: number; left?: number; right?: number };
  /** CSS font-style; maps V1 isItalic. */
  fontStyle?: 'normal' | 'italic';
  /** CSS text-decoration; maps V1 isUnderlined / isStrikethrough. */
  textDecoration?: 'none' | 'underline' | 'line-through' | 'underline line-through';
  /** Vertical placement of content within the component box; maps V1 verticalAlign.
   *  Applied to all scalar component kinds (text, number, date, enum, badge) so
   *  mixed-kind layouts behave consistently. */
  verticalAlign?: 'top' | 'center' | 'bottom';
  /**
   * Drop-shadow depth. Plain enum like `fontWeight` — not a ValueSource.
   * Omitted or `0` = flat (default). `1` = soft, `2` = raised.
   */
  elevation?: 0 | 1 | 2;
  /**
   * Light-source edge shading. Plain enum like `fontWeight` — not a ValueSource.
   * Omitted = flat single-color border (default). `raised` = light top/left;
   * `inset` = recessed (inverted).
   */
  bevel?: 'raised' | 'inset';
}

/** Declarative visibility rule controlling whether a component is rendered. */
export interface VisibilityRule {
  /** Whether the component is visible; static, binding, or expression-driven. */
  visible: ValueSource<boolean>;
}

/** Declarative rule controlling whether a component accepts user interaction. */
export interface EnabledRule {
  /** Whether the component is interactive; static, binding, or expression-driven. */
  enabled: ValueSource<boolean>;
}

/** Gesture-triggered interaction bindings for a component. */
export interface ComponentInteractionBindings {
  /** Handler invoked on a single press or tap (action id or script id). */
  onPress?: ComponentPressHandler;
  /**
   * Additional press handlers when more than one action/script is wired to the same
   * component press surface. Export uses this when multiple handlers exist; the
   * first handler is also mirrored on `onPress` / `button.action` for compatibility.
   */
  onPressHandlers?: readonly ComponentPressHandler[];
  /** Handler invoked on a long press or hold (action id or script id). */
  onLongPress?: ComponentPressHandler;
  /** Handler invoked when an editable bound value is committed. */
  onChange?: ComponentPressHandler;
  /**
   * Additional change handlers when more than one action/script is wired to the
   * same editable commit surface. Export uses this when multiple handlers exist;
   * the first handler is also mirrored on `onChange` for compatibility.
   */
  onChangeHandlers?: readonly ComponentPressHandler[];
  /** Handler invoked once when the component finishes loading in the adapter. */
  onLoad?: ComponentPressHandler;
}

/** Accessibility metadata for a component node. */
export interface ComponentSemantics {
  /** Localized label read aloud by screen readers. */
  accessibilityLabel?: LocalizedText;
  /** Localized hint providing additional context for screen reader users. */
  accessibilityHint?: LocalizedText;
  /** ARIA-compatible accessibility role hint. */
  accessibilityRole?: string;
}

/**
 * Component press handler — same contract as lifecycle EventHandler.
 * Legacy JSON may use `{ actionId }` without `kind`.
 */
export type ComponentPressHandler = PressHandlerInput;

/**
 * @deprecated Use ComponentPressHandler. Kept for editor imports and legacy `{ actionId }` payloads.
 */
export type ComponentActionRef = ComponentPressHandler;

/** Format options for displaying text, numeric, or date values. */
export interface TextFormat {
  /** Printf-style display pattern string. */
  pattern?: string;
  /** Localized string prepended to the formatted value. */
  prefix?: LocalizedText;
  /** Localized string appended to the formatted value. */
  suffix?: LocalizedText;
  /** Numeric display format preset. */
  numberFormat?: 'integer' | 'decimal' | 'percent' | 'currency';
  /**
   * Display mode hint for datetime values (`date` | `time`, or a token string
   * containing `yyyy` / `hh:mm`). Not a strftime/pattern engine — actual
   * formatting uses Language → Date & time format prefs.
   */
  dateFormat?: string;
}

/** A component that renders a string value as styled text. */
export interface TextComponent extends ComponentBase {
  /** Component type discriminant. */
  type: 'text';
  /** Text-specific display and editing configuration. */
  text: {
    /** Value source for the string content (static arm may be LocalizedText). */
    source: ValueSource<LocalizedText>;
    /**
     * Optional href for tappable text (URL, mailto, tel, or item:…).
     * Bind to itemField linkOut or a category url property; display text stays `source`.
     */
    href?: ValueSource<string>;
    /** Optional display format. */
    format?: TextFormat;
    /** Whether the user may edit this field in-place. */
    editable?: boolean;
    /** Localized placeholder shown when the value is empty. */
    placeholder?: LocalizedText;
    /** Whether the text wraps across multiple lines. */
    multiline?: boolean;
    /** Maximum visible lines; maps V1 maxLineNumber. Clamps display when multiline is true.
     *  When absent or null, no line limit is applied (if multiline) or 1 line is shown. */
    maxLines?: number;
    /** Overflow strategy when maxLines is exceeded.
     *  `ellipsis` = truncate end (RN tail); `ellipsisStart` = truncate start (RN head). */
    overflow?: 'clip' | 'ellipsis' | 'ellipsisStart' | 'wrap';
  };
}

/** A component that renders a numeric value. */
export interface NumberComponent extends ComponentBase {
  /** Component type discriminant. */
  type: 'number';
  /** Number-specific display and editing configuration. */
  number: {
    /** Value source for the numeric content. */
    source: ValueSource<number>;
    /** Optional display format. */
    format?: TextFormat;
    /**
     * Maximum fraction digits shown when formatting the value for display
     * and for the editable input body (after commit / external updates).
     * Clamps noisy floats (e.g. slider-driven localState). Omit for unlimited.
     * Integer in 0–20; `0` shows no fractional part.
     */
    maxDigits?: number;
    /** Whether the user may edit this value in-place. */
    editable?: boolean;
    /** Localized placeholder shown when the value is empty. */
    placeholder?: LocalizedText;
  };
}

/** A component that renders a boolean value as a toggle, checkbox, or icon. */
export interface BooleanComponent extends ComponentBase {
  /** Component type discriminant. */
  type: 'boolean';
  /** Boolean-specific display and editing configuration. */
  boolean: {
    /** Value source for the boolean state. */
    source: ValueSource<boolean>;
    /** Label shown when the value is true. Empty/missing = no true-side text. */
    trueLabel?: LocalizedText;
    /** Label shown when the value is false. Empty/missing = no false-side text. */
    falseLabel?: LocalizedText;
    /** Visual control variant. */
    variant?: 'toggle' | 'checkbox' | 'icon';
    /**
     * Label layout when any label text is present (DEC-063).
     * - `trailing` — current-state label to the right of the control (default).
     * - `sides` — false left / true right of a switch (`toggle` only).
     * Ignored when both labels are empty (control fills the layout box).
     */
    labelPlacement?: 'trailing' | 'sides';
    /**
     * Control chrome colors (DEC-063). Independent of StyleBlock so text / frame /
     * background chrome can be authored separately.
     * - `trueColor` — on/checked fill
     * - `falseColor` — off/unchecked fill
     * - `outlineColor` — control outline / off track edge
     */
    trueColor?: ValueSource<string>;
    falseColor?: ValueSource<string>;
    outlineColor?: ValueSource<string>;
    /** Whether the user may toggle this value in-place. */
    editable?: boolean;
  };
}

/** Picker UI mode for editable date components. Defaults to `'datetime'` when omitted. */
export type DatePickerMode = 'date' | 'time' | 'datetime';

/** A component that renders a date or datetime string value. */
export interface DateComponent extends ComponentBase {
  /** Component type discriminant. */
  type: 'date';
  /** Date-specific display and editing configuration. */
  date: {
    /** Value source for the ISO 8601 date string. */
    source: ValueSource<string>;
    /**
     * Display mode preset: `date` | `time` (anything else → datetime).
     * Not a custom pattern string — app Language date/time prefs apply.
     */
    format?: string;
    /** Whether the user may pick a new date in-place. */
    editable?: boolean;
    /**
     * Native/web picker chrome when `editable` is true.
     * Defaults to `'datetime'` when omitted (backward compatible).
     */
    pickerMode?: DatePickerMode;
  };
}

/** A component that renders an enum property value as a label, chip, or icon. */
export interface EnumComponent extends ComponentBase {
  /** Component type discriminant. */
  type: 'enum';
  /** Enum-specific display and editing configuration. */
  enum: {
    /** Value source for the selected enum option ID. */
    source: ValueSource<string>;
    /** Category ID that owns the referenced enum property. */
    categoryId: string;
    /** Property ID of the enum property definition. */
    propertyId: string;
    /** Whether the user may change the selected option in-place. */
    editable?: boolean;
    /** Visual display mode for the enum value. */
    displayMode?: 'label' | 'chip' | 'icon';
  };
}

/** A component that renders a string-encoded dropdown (selection + options in one bound value). */
export interface StringSelectComponent extends ComponentBase {
  /** Component type discriminant. */
  type: 'stringSelect';
  /** String-select display and editing configuration. */
  stringSelect: {
    /** Value source for the full encoded string (`selected;option1;option2;...`). */
    source: ValueSource<string>;
    /** Delimiter between encoded segments. Default: ';'. */
    separator?: string;
    /** Whether the user may change the selected option in-place. */
    editable?: boolean;
    /** Visual display mode for the selected value. */
    displayMode?: 'label' | 'chip';
  };
}

/** A component that renders an image from a value source or asset reference. */
export interface ImageComponent extends ComponentBase {
  /** Component type discriminant. */
  type: 'image';
  /** Image-specific display and editing configuration. */
  image: {
    /**
     * Image URI ValueSource, or a static AssetRef.
     * AssetRef (inline / manifest / relativePath) is template chrome — painted from the
     * template only; never copied into `item.imageUris` at creation.
     * Bind `indexedItemField imageUris[n]` for item-owned gallery photos.
     */
    source: ValueSource<string> | AssetRef;
    /**
     * Optional href (URL, mailto, tel, or item:…). Same channel as text/button.
     * Bind to itemField linkOut for website video/link blocks.
     * Host: tap opens the URL (no embed). Public share: YouTube URLs paint as
     * a nocookie iframe; other hrefs wrap the image in `<a>`.
     * When set, tap-to-open wins over in-place photo replace (`editable`).
     */
    href?: ValueSource<string>;
    /** How the image fills its layout bounds. */
    fit?: 'contain' | 'cover' | 'fill';
    /** Whether the user may replace the image in-place (gallery/camera → imageUris slot). */
    editable?: boolean;
    /** Fallback rendering when no image is available. */
    fallback?: 'none' | 'placeholder' | 'icon';
  };
}

/**
 * Template-owned decorative SVG placed on the item layout surface.
 * Distinct from `image` (item gallery photos) and element-level `svgGraphics`.
 * Never materializes into `item.imageUris` at creation — paint from the live template AssetRef.
 *
 * Optional paint (`fill` / `stroke` / `strokeWidth`) is fully bindable via ValueSource.
 * When set, the runtime rewrites presentation attributes on the authored SVG
 * (skips `none` and `url(...)` paints). Omitted fields leave the asset unchanged.
 */
export interface SvgComponent extends ComponentBase {
  /** Component type discriminant. */
  type: 'svg';
  /** SVG-specific display configuration. */
  svg: {
    /** Template-owned SVG asset; inline, manifest, or relativePath. */
    source: AssetRef;
    /** How the SVG fills its layout bounds. */
    fit?: 'contain' | 'cover' | 'fill';
    /** When 'none' (default), touches pass through to components above. */
    pointerEvents?: 'none' | 'auto';
    /** Path fill color; static, binding, or expression-driven. */
    fill?: ValueSource<string>;
    /** Path stroke color; static, binding, or expression-driven. */
    stroke?: ValueSource<string>;
    /** Stroke thickness in device pixels; static, binding, or expression-driven. */
    strokeWidth?: ValueSource<number>;
  };
}

/**
 * Template-owned HTML / iframe embed placed on the item layout surface.
 *
 * The value may be a bare `https://` URL, a single `<iframe …></iframe>`
 * snippet, or script-free HTML. Authored `<script>`, `on*` handlers and
 * `javascript:` / `data:` URLs are stripped by `normalizeHtmlEmbed` — see
 * `v2/utils/htmlEmbed.ts`.
 *
 * Host: renders behind a poster + tap-to-activate frame (third-party sources
 * require the app privacy gate; containd.space is first-party and loads
 * directly). Public share: the same normalized embed is baked into the frozen
 * descriptor and painted filled; third-party iframes keep the site-consent
 * gate. A lone iframe fills the component box.
 */
export interface HtmlComponent extends ComponentBase {
  /** Component type discriminant. */
  type: 'html';
  /** HTML / iframe embed configuration. */
  html: {
    /** Embed source: bare https URL, `<iframe>` snippet, or script-free HTML. */
    source: ValueSource<string>;
    /** How the embed fills its layout bounds. Default `'fill'`. */
    fit?: 'fill' | 'contain';
    /** Fallback rendering when the source is empty/invalid. */
    fallback?: 'none' | 'placeholder';
  };
}

/** QR error-correction / redundancy level (ISO/IEC 18004). Static authoring only. */
export type QrCodeErrorCorrection = 'L' | 'M' | 'Q' | 'H';

export const QR_CODE_ERROR_CORRECTION_LEVELS: readonly QrCodeErrorCorrection[] = [
  'L',
  'M',
  'Q',
  'H',
];

/**
 * Live-generated QR code on the item layout surface (DEC-082).
 * Distinct from gallery `image`, decorative `svg`, and label-system / share QR UI.
 *
 * Payload and colors are bindable ValueSources (full DataRef surface, same as text).
 * Error correction and fit are static. Runtime bakes SVG into descriptor IR for
 * host paint and public-share freeze.
 */
export interface QrCodeComponent extends ComponentBase {
  /** Component type discriminant. */
  type: 'qrCode';
  /** QR-specific display configuration. */
  qrCode: {
    /** Payload string encoded into the QR (URL, vCard, plain text, …). */
    source: ValueSource<string>;
    /** Module / ink color (CSS `#RRGGBB`, `#RRGGBBAA`, or `rgba(...)`). Bindable. */
    color?: ValueSource<string>;
    /** Quiet-zone / background color. Bindable. Alpha is allowed. */
    backgroundColor?: ValueSource<string>;
    /** Redundancy level. Static only. Default `'M'`. */
    errorCorrection?: QrCodeErrorCorrection;
    /** How the square code fills layout bounds. Static. Default `'contain'`. */
    fit?: 'contain' | 'cover' | 'fill';
  };
}

/**
 * Glyph shape used for each rating unit (filled/empty Unicode pairs).
 * Omit → stars. See `ratingGlyphs.ts`.
 */
export type RatingGlyphId = 'stars' | 'dots' | 'bars' | 'hearts';

/** A component that renders a star or numeric rating. */
export interface RatingComponent extends ComponentBase {
  /** Component type discriminant. */
  type: 'rating';
  /** Rating-specific display and editing configuration. */
  rating: {
    /** Value source for the current rating value. */
    source: ValueSource<number>;
    /**
     * Maximum rating value (determines total stars/steps).
     * Fully bindable — static, itemField, expression, etc.
     * Legacy bare numbers are accepted at resolve time.
     */
    maxRating?: ValueSource<number>;
    /** Increment between rating steps. */
    step?: number;
    /** Whether the user may adjust the rating in-place. */
    editable?: boolean;
    /**
     * Unit glyph shape (stars / dots / bars / hearts).
     * Static enum — not bindable. Omit defaults to stars.
     */
    glyph?: RatingGlyphId;
  };
}

/**
 * Binds a CSS color string and paints its layout box with that color on canvas.
 *
 * Authoring patterns (see `docs/V2-Authoring-Guide.md` → Color components):
 * - **Background fill** (`editable: false`): full-layout layer behind other components;
 *   uses `pointerEvents: none` so taps reach fields above. Typical zIndex: 0.
 * - **Color picker** (`editable: true`): same fill in its layout rect; tap opens the
 *   host color prompt. Often a small top-left control sharing the same binding as the
 *   background layer.
 *
 * The runtime does not render a separate swatch tile or hex label — the bound color is
 * the fill. Use `style.borderColor` / `style.borderWidth` on the component for picker chrome.
 */
export interface ColorComponent extends ComponentBase {
  /** Component type discriminant. */
  type: 'color';
  /** Color-specific display and editing configuration. */
  color: {
    /** Value source for the color string (usually a `categoryProperty` binding). */
    source: ValueSource<string>;
    /**
     * When true, tapping opens the color picker and commits via `binding.read` (same ref as
     * `color.source` when bound). When false, display-only; do not declare `binding.read`.
     */
    editable?: boolean;
    /** Expected color string format written by the picker (default `hex`). */
    format?: 'hex' | 'rgba';
  };
}

/** Optional confirmation dialog shown before a button action is executed. */
export interface ConfirmDialog {
  /** Localized dialog title. */
  title: LocalizedText;
  /** Localized body message. */
  message?: LocalizedText;
  /** Localized label for the confirm button. */
  confirmLabel?: LocalizedText;
  /** Localized label for the cancel button. */
  cancelLabel?: LocalizedText;
}

/** A tappable button component that invokes a template action. */
export interface ButtonComponent extends ComponentBase {
  /** Component type discriminant. */
  type: 'button';
  /** Button-specific display and action configuration. */
  button: {
    /** Value source for the button label text (static arm may be LocalizedText). */
    label: ValueSource<LocalizedText>;
    /**
     * Optional href for tappable buttons (URL, mailto, tel, or item:…).
     * Bind to itemField linkOut or a category url property; display text stays `label`.
     * Host: href opens and press action/script still fire. Public share: href only.
     */
    href?: ValueSource<string>;
    /** Optional icon asset displayed alongside the label. */
    icon?: AssetRef;
    /** Visual style variant for the button. */
    variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
    /** Handler invoked when the button is pressed (action id or script id). */
    action: ComponentPressHandler;
    /** Optional confirmation dialog shown before the action executes. */
    confirm?: ConfirmDialog;
  };
}

/**
 * Catalog glyph in a defined StyleBlock box (no label text).
 *
 * Paint: `style.color` → glyph stroke; `style.backgroundColor` → fill;
 * border / radius / padding via StyleBlock. Optional `action` makes it an icon button.
 */
export interface IconComponent extends ComponentBase {
  /** Component type discriminant. */
  type: 'icon';
  /** Icon-specific display and optional press configuration. */
  icon: {
    /**
     * Catalog icon id (kebab-case), e.g. `"trash-2"`.
     * Resolved via the shared Lucide catalog — not an AssetRef.
     */
    name: ValueSource<string>;
    /** When present, the icon is pressable (icon button). */
    action?: ComponentPressHandler;
    /** Optional confirmation dialog shown before the action executes. */
    confirm?: ConfirmDialog;
  };
}

/** A component that renders a colored badge or chip with a value. */
export interface BadgeComponent extends ComponentBase {
  /** Component type discriminant. */
  type: 'badge';
  /** Badge-specific display configuration. */
  badge: {
    /** Value source for the badge content (string or number). */
    source: ValueSource<string | number>;
    /** Foreground text color; static or expression-driven. */
    color?: ValueOrExpression<string>;
    /** Badge background color; static or expression-driven. */
    backgroundColor?: ValueOrExpression<string>;
  };
}

/** A component that renders a progress bar or linear indicator. */
export interface ProgressComponent extends ComponentBase {
  /** Component type discriminant. */
  type: 'progress';
  /** Progress-specific display configuration. */
  progress: {
    /** Value source for the current progress value. */
    value: ValueSource<number>;
    /** Value source for the maximum bound. */
    max?: ValueSource<number>;
    /** Value source for the minimum bound. */
    min?: ValueSource<number>;
    /** Fill color for the progress track; static or expression-driven. */
    color?: ValueOrExpression<string>;
  };
}

/** Live countdown / stopwatch display that ticks while mounted. */
export interface TimerDisplayComponent extends ComponentBase {
  type: 'timerDisplay';
  timerDisplay: {
    /** Optional timer id; omit = primary timer for the current item. */
    timerId?: ValueSource<string>;
    /** Highest unit shown; higher magnitudes roll into this unit uncapped. */
    maxUnit?: 'day' | 'hour' | 'minute' | 'second';
  };
}

/**
 * Interactive range slider with a draggable knob (DEC-059).
 *
 * Chrome via StyleBlock (standard behaviour):
 * - `style.color` → filled track portion + knob (text/foreground)
 * - `style.backgroundColor` → track trough
 * - `style.borderColor` / `style.borderWidth` → edge
 *
 * Out-of-bounds bound values are indicated visually but not clamped; the user
 * may still drag the knob to set an in-range value.
 */
export interface SliderComponent extends ComponentBase {
  /** Component type discriminant. */
  type: 'slider';
  /** Slider-specific display and editing configuration. */
  slider: {
    /** Value source for the current slider value. */
    source: ValueSource<number>;
    /** Value source for the minimum bound. Default: 0. */
    min?: ValueSource<number>;
    /** Value source for the maximum bound. Default: 100. */
    max?: ValueSource<number>;
    /** Snap increment; when omitted, integer mode defaults to 1. */
    step?: number;
    /** Integer vs continuous float. Default: 'float'. */
    valueType?: 'integer' | 'float';
    /** Track orientation within the layout box. Default: 'horizontal'. */
    orientation?: 'horizontal' | 'vertical';
    /** Whether the user may drag the knob. Default: true. */
    editable?: boolean;
  };
}

/** A layout container that groups and clips child component nodes. */
export interface GroupComponent extends ComponentBase {
  /** Component type discriminant. */
  type: 'group';
  /** Group-specific layout configuration. */
  group: {
    /** Child component nodes rendered inside this group. */
    children: ComponentNode[];
    /** Whether child content is clipped to the group layout bounds. */
    clipContent?: boolean;
  };
}

/** An invisible spacer component used to create gaps in a layout. */
export interface SpacerComponent extends ComponentBase {
  /** Component type discriminant. */
  type: 'spacer';
  /** Optional spacer configuration. */
  spacer?: {
    /** Flex growth factor for the spacer in a flex container. */
    flex?: number;
  };
}

/**
 * Visual drag affordance for storage-canvas item surfaces (host-only).
 * Renders a grip icon; captures pointer hits at runtime so a handle stacked
 * above an editable field starts item drag instead of field edit. Omitted
 * from public-share freeze and not painted on the website.
 */
export interface DragHandleComponent extends ComponentBase {
  /** Component type discriminant. */
  type: 'dragHandle';
  /** Reserved for forward-compatible extension; no configurable fields in MVP. */
  dragHandle?: Record<string, never>;
}

/**
 * Decorative horizontal or vertical line segment within the layout box.
 * Stroke thickness is independent of layout height/width (unlike a thin color fill).
 */
export interface LineComponent extends ComponentBase {
  /** Component type discriminant. */
  type: 'line';
  /** Line-specific stroke configuration. */
  line: {
    /** Line direction within the layout box. Default: 'horizontal'. */
    orientation?: 'horizontal' | 'vertical';
    /** Stroke thickness in device pixels. Default: { kind: 'static', value: 2 }. */
    strokeWidth?: ValueSource<number>;
    /** Stroke color. Default: { kind: 'static', value: '#64748b' }. */
    color?: ValueSource<string>;
  };
}

/**
 * In-layout table (DEC-077 list source + dual-mode binding parity with chart).
 *
 * Data resolution precedence:
 * 1. `source` resolves to an Array → list-backed matrix (first row = headers); ignores tableSource for data.
 * 2. Else `tableSource` bound to `table` / `fileTable` → CTBL or FileTableManifest (optional `itemFrom`).
 *
 * Author both fields when scripts alternate between ephemeral matrix and a DataItem table link;
 * unset / non-array `source` falls through to `tableSource`.
 */
export interface TableComponent extends ComponentBase {
  /** Component type discriminant. */
  type: 'table';
  /** Table-specific display and editing configuration. */
  table: {
    /**
     * DEC-077 list-backed data: localState / itemVariable `list` holding a flat list or
     * list-of-lists (first row = column headers). When present and resolved to an Array,
     * wins over tableSource.
     */
    source?: ValueSource<unknown>;
    /**
     * Binding to categoryProperty or itemVariable holding CTBL base64
     * (`table`) or a FileTableManifest JSON string (`fileTable`).
     * Supports DEC-074 `itemFrom` for cross-item DataItem links.
     */
    tableSource?: ValueSource<string>;
    /** Whether the user may edit cells / append rows in-place (ignored for list-backed binds). */
    editable?: boolean;
    /**
     * Required for CTBL string itemVariable binds (no category schema).
     * Ignored for list-backed localState/itemVariable arrays (headers from first row).
     */
    schema?: TablePropertySchema;
    /** Maximum logical rows to display on canvas (default: all). */
    visibleRowCount?: number;
  };
}

/**
 * Scrollable list of entries from a list-typed value source.
 * Selection is a separate 0-based integer index; `editable` enables tap-to-select only
 * (membership/order edits belong in Edit Entry / scripts).
 */
export interface ListViewComponent extends ComponentBase {
  /** Component type discriminant. */
  type: 'listView';
  /** ListView display and selection configuration. */
  listView: {
    /**
     * Resolves to a heterogeneous JSON array (category `list` or itemVariable `list`).
     * Binding `itemField` `documents` is allowed: paint uses display names plus optional
     * aligned `subtitles` (`datetime · filesize`). Published Spaces omit attachments.
     */
    source: ValueSource<unknown>;
    /** 0-based selected row index; any negative / out-of-range / missing → no highlight (`-1` preferred for none). */
    selectionSource: ValueSource<number>;
    /** When true, tapping a row writes the index to `selectionSource`. */
    editable?: boolean;
  };
}

/**
 * Inline layout chart types (DEC-056).
 * Frequencies are separate chartType tokens (not seriesMode).
 * `scatter` = markers-only XY; `timeline` = datetime X; `line` = numeric X.
 * Multi-series / dual-Y apply only to line | scatter | timeline.
 */
export type LayoutChartType =
  | 'line'
  | 'scatter'
  | 'timeline'
  | 'bar'
  | 'barFrequencies'
  | 'pie'
  | 'pieFrequencies'
  | 'histogram'
  | 'box';

/** Bar/histogram/box bar direction. */
export type ChartOrientation = 'vertical' | 'horizontal';

/** Which Y scale a series uses (XY charts only). */
export type ChartYAxisSide = 'primary' | 'secondary';

/**
 * One Y series on a layout chart (DEC-056 multi-series).
 * Table path uses `yColumn`; list path uses `ySource`.
 */
export type ChartSeriesDef = {
  id: string;
  label?: ValueSource<LocalizedText>;
  /** Column id for Y values (table path). */
  yColumn?: string;
  /** Non-table Y series: list, scalar, or static CSV. */
  ySource?: ValueSource<unknown>;
  color?: ValueSource<string>;
  strokeWidth?: ValueSource<number>;
  /** Default `primary`. Secondary only valid for line/scatter/timeline. */
  yAxis?: ChartYAxisSide;
  /** Per-series Y filter minimum; omit → Auto. */
  yMin?: ValueSource<number>;
  /** Per-series Y filter maximum; omit → Auto. */
  yMax?: ValueSource<number>;
};

/**
 * In-layout chart (DEC-056 + dual-mode binding + multi-series XY + DEC-078 list source).
 *
 * Data resolution precedence:
 * 1. `source` resolves to an Array → list-backed matrix/flat coercion (DEC-078); ignores table/list column binds for data.
 * 2. Else `tableSource` bound to `table` / `fileTable` → extract `xColumn` + each series `yColumn`.
 * 3. Else `xSource` + each series `ySource` (list, scalar, or static CSV string).
 *
 * Explicit xMin/xMax and per-series yMin/yMax act as inclusive **data filters**; omit = Auto.
 *
 * Legacy single `yColumn`/`ySource`/`seriesColor`/`yMin`/`yMax` migrate to `series[0]` at load.
 * Legacy `seriesMode: 'frequencies'` on bar/pie is migrated to `barFrequencies` /
 * `pieFrequencies` at load/render time and must not be authored going forward.
 */
export interface ChartComponent extends ComponentBase {
  /** Component type discriminant. */
  type: 'chart';
  /** Chart-specific data binding and presentation configuration. */
  chart: {
    /** Visualization kind (static, binding, or expression). */
    chartType: ValueSource<LayoutChartType>;
    /** Bar/histogram/box orientation. Default: 'vertical'. */
    orientation?: ChartOrientation;
    /** Histogram bin count. Default runtime: 10. */
    binCount?: ValueSource<number>;
    /**
     * DEC-078 list-backed data: localState / itemVariable `list` holding a flat list or
     * list-of-lists (data rows only, no header). Shape rules derive X/Y/multi-series.
     * When present and resolved to an Array, wins over tableSource / xSource / ySource.
     */
    source?: ValueSource<unknown>;
    /**
     * Binding to categoryProperty or itemVariable holding CTBL base64
     * (`table`) or a FileTableManifest JSON string (`fileTable`).
     */
    tableSource?: ValueSource<string>;
    /** Required when tableSource is itemVariable (no category schema available). */
    schema?: TablePropertySchema;
    /** Column id for shared X values (table path). */
    xColumn?: string;
    /**
     * Named Y series (required after migrate; length ≥ 1).
     * Multi-series / dual-Y only for line | scatter | timeline (max 8).
     * Bar/pie/histogram/box must have exactly one series.
     */
    series: ChartSeriesDef[];
    /**
     * @deprecated Prefer `series[0].yColumn`. Migrated at load then stripped.
     */
    yColumn?: string;
    /** Optional column id for per-point colors (single-series categorical charts). */
    colorColumn?: string;
    /** Optional column id for per-point / slice labels. */
    labelColumn?: string;
    /** Non-table shared X series: list, scalar, or static CSV. */
    xSource?: ValueSource<unknown>;
    /**
     * @deprecated Prefer `series[0].ySource`. Migrated at load then stripped.
     */
    ySource?: ValueSource<unknown>;
    /** Chart title. */
    title?: ValueSource<string>;
    /** X-axis label; omit or empty → auto from column header. */
    xAxisLabel?: ValueSource<string>;
    /** Primary Y-axis label; omit or empty → auto from column header. */
    yAxisLabel?: ValueSource<string>;
    /** Secondary Y-axis label (XY dual-Y only). */
    ySecondaryAxisLabel?: ValueSource<string>;
    /** Explicit X filter minimum; omit → Auto (no filter). */
    xMin?: ValueSource<number>;
    /** Explicit X filter maximum; omit → Auto (no filter). */
    xMax?: ValueSource<number>;
    /**
     * @deprecated Prefer `series[0].yMin`. Migrated at load then stripped.
     */
    yMin?: ValueSource<number>;
    /**
     * @deprecated Prefer `series[0].yMax`. Migrated at load then stripped.
     */
    yMax?: ValueSource<number>;
    /** Default stroke for series that omit strokeWidth. */
    strokeWidth?: ValueSource<number>;
    /**
     * @deprecated Prefer `series[0].color`. Migrated at load then stripped.
     */
    seriesColor?: ValueSource<string>;
    /** Axis / grid stroke color. */
    axisColor?: ValueSource<string>;
  };
}

/** Discriminated union of all supported V2 component node variants. */
export type ComponentNode =
  | TextComponent
  | NumberComponent
  | BooleanComponent
  | DateComponent
  | EnumComponent
  | StringSelectComponent
  | ImageComponent
  | SvgComponent
  | HtmlComponent
  | QrCodeComponent
  | RatingComponent
  | ColorComponent
  | ButtonComponent
  | IconComponent
  | BadgeComponent
  | ProgressComponent
  | TimerDisplayComponent
  | SliderComponent
  | GroupComponent
  | SpacerComponent
  | DragHandleComponent
  | LineComponent
  | TableComponent
  | ChartComponent
  | ListViewComponent;
